import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from '@kostbar/i18n'
import {
  useCreateRestaurant,
  useUpdateRestaurant,
} from '../hooks/use-restaurants-admin'
import { normalizeCompanyProfile } from '../lib/company-profile-normalize'
import { ImageUploadField } from './image-upload-field'
import type { TFunction } from '@kostbar/i18n'
import type { CompanyProfile } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'

const UID_RE = /^ATU\d{8}$/
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

const addressSchema = z.object({
  street: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
})

const buildCompanyProfileSchema = (tRestaurants: TFunction<'restaurants'>) =>
  z.object({
    legal: z.object({
      name: z
        .string()
        .min(1, tRestaurants('form.validation.legalNameRequired')),
      uid: z
        .string()
        .optional()
        .refine(
          (v) => !v || !v.trim() || UID_RE.test(v.trim()),
          tRestaurants('form.validation.uidInvalid'),
        ),
      registerId: z.string().optional(),
      owner: z.string().optional(),
      address: z.object({
        street: z
          .string()
          .min(1, tRestaurants('form.validation.streetRequired')),
        postalCode: z
          .string()
          .min(1, tRestaurants('form.validation.postalCodeRequired')),
        city: z.string().min(1, tRestaurants('form.validation.cityRequired')),
        country: z.string().optional(),
      }),
    }),
    location: z.object({ address: addressSchema }),
    contact: z.object({
      phone: z.string().optional(),
      email: z
        .string()
        .optional()
        .refine(
          (v) => !v || !v.trim() || EMAIL_RE.test(v.trim()),
          tRestaurants('form.validation.emailInvalid'),
        ),
      websiteUrl: z.string().optional(),
    }),
    media: z.object({
      thumbnailUrl: z.string().optional(),
      headerImageUrl: z.string().optional(),
    }),
    receipt: z.object({ hoursText: z.string().optional() }),
    urlSlug: z
      .string()
      .optional()
      .refine(
        (v) =>
          !v || !v.trim() || /^[a-z0-9-]{2,30}$/.test(v.trim().toLowerCase()),
        tRestaurants('form.validation.urlSlugInvalid'),
      ),
  })

const buildRestaurantSchema = (tRestaurants: TFunction<'restaurants'>) =>
  z.object({
    name: z.string().min(1, tRestaurants('form.validation.nameRequired')),
    code: z.string().min(1, tRestaurants('form.validation.codeRequired')),
    timezone: z.string().optional(),
    onlineOrderingEnabled: z.boolean().optional(),
    inventoryTrackingEnabled: z.boolean().optional(),
    inventoryCountShowExpected: z.boolean().optional(),
    visibility: z.enum(['PUBLIC', 'INTERNAL']),
    enrollmentCode: z.string().optional(),
    webbillReceiverRef: z.string().optional(),
    defaultSafetyBufferQty: z
      .number()
      .int(tRestaurants('form.validation.integer'))
      .min(0, tRestaurants('form.validation.minZero'))
      .optional(),
    companyProfile: buildCompanyProfileSchema(tRestaurants),
  })

type RestaurantFormSchema = ReturnType<typeof buildRestaurantSchema>
type RestaurantFormInput = z.input<RestaurantFormSchema>
export type RestaurantFormData = z.output<RestaurantFormSchema>

interface RestaurantFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initial?: Partial<
    Omit<
      RestaurantFormData,
      'enrollmentCode' | 'webbillReceiverRef' | 'companyProfile'
    >
  > & {
    id?: number
    enrollmentCode?: string | null
    webbillReceiverRef?: string | null
    companyProfile?: CompanyProfile
  }
}

const emptyAddress = { street: '', postalCode: '', city: '', country: '' }

const emptyCompanyProfile = () => ({
  legal: {
    name: '',
    uid: '',
    registerId: '',
    owner: '',
    address: { ...emptyAddress },
  },
  location: { address: { ...emptyAddress } },
  contact: { phone: '', email: '', websiteUrl: '' },
  media: { thumbnailUrl: '', headerImageUrl: '' },
  receipt: { hoursText: '' },
  urlSlug: '',
})

function toFormCompanyProfile(initial?: CompanyProfile) {
  const empty = emptyCompanyProfile()
  if (!initial) return empty
  return {
    legal: {
      name: initial.legal?.name ?? '',
      uid: initial.legal?.uid ?? '',
      registerId: initial.legal?.registerId ?? '',
      owner: initial.legal?.owner ?? '',
      address: { ...emptyAddress, ...(initial.legal?.address ?? {}) },
    },
    location: {
      address: { ...emptyAddress, ...(initial.location?.address ?? {}) },
    },
    contact: {
      phone: initial.contact?.phone ?? '',
      email: initial.contact?.email ?? '',
      websiteUrl: initial.contact?.websiteUrl ?? '',
    },
    media: {
      thumbnailUrl: initial.media?.thumbnailUrl ?? '',
      headerImageUrl: initial.media?.headerImageUrl ?? '',
    },
    receipt: { hoursText: initial.receipt?.hoursText ?? '' },
    urlSlug: initial.urlSlug ?? '',
  }
}

export function RestaurantForm({
  open,
  onOpenChange,
  initial,
}: RestaurantFormProps) {
  const { t: tRestaurants } = useTranslation('restaurants')
  const { t: tCommon } = useTranslation('common')
  const isEdit = Boolean(initial?.id)
  const { can } = usePermissions()
  const canManage = can(ACTIONS.RESTAURANT_MANAGE)
  const createMutation = useCreateRestaurant()
  const updateMutation = useUpdateRestaurant()

  const form = useForm<RestaurantFormInput, unknown, RestaurantFormData>({
    resolver: zodResolver(buildRestaurantSchema(tRestaurants)),
    defaultValues: {
      name: '',
      code: '',
      timezone: 'Europe/Vienna',
      onlineOrderingEnabled: false,
      inventoryTrackingEnabled: true,
      inventoryCountShowExpected: true,
      visibility: 'PUBLIC',
      enrollmentCode: '',
      webbillReceiverRef: '',
      defaultSafetyBufferQty: 0,
      companyProfile: emptyCompanyProfile(),
    },
  })

  const visibility = form.watch('visibility')

  useEffect(() => {
    if (open) {
      form.reset({
        name: initial?.name ?? '',
        code: initial?.code ?? '',
        timezone: initial?.timezone ?? 'Europe/Vienna',
        onlineOrderingEnabled: initial?.onlineOrderingEnabled ?? false,
        inventoryTrackingEnabled: initial?.inventoryTrackingEnabled ?? true,
        inventoryCountShowExpected: initial?.inventoryCountShowExpected ?? true,
        visibility: initial?.visibility ?? 'PUBLIC',
        enrollmentCode: initial?.enrollmentCode ?? '',
        webbillReceiverRef: initial?.webbillReceiverRef ?? '',
        defaultSafetyBufferQty: initial?.defaultSafetyBufferQty ?? 0,
        companyProfile: toFormCompanyProfile(initial?.companyProfile),
      })
    }
  }, [open])

  const onSubmit = (data: RestaurantFormData) => {
    if (!canManage) return
    const companyProfile = normalizeCompanyProfile(data.companyProfile)
    const enrollmentCode = data.enrollmentCode?.trim() ?? ''
    const webbillReceiverRef = data.webbillReceiverRef?.trim() ?? ''
    if (isEdit && initial?.id) {
      // code immutable; enrollmentCode and webbillReceiverRef handled explicitly below.
      const {
        code: _,
        enrollmentCode: __,
        webbillReceiverRef: ___,
        ...rest
      } = data
      const body: Parameters<typeof updateMutation.mutate>[0]['body'] = {
        ...rest,
        companyProfile,
      }
      // Only send enrollmentCode when the user actually edited it: an empty
      // string clears the code, while omitting it leaves it unchanged.
      if (form.formState.dirtyFields.enrollmentCode) {
        body.enrollmentCode = enrollmentCode
      }
      // Same contract for the webbill receiver ref: clearing it takes the store back out of the
      // delivery import, so it must be possible — but only when the field was actually touched.
      if (form.formState.dirtyFields.webbillReceiverRef) {
        body.webbillReceiverRef = webbillReceiverRef
      }
      updateMutation.mutate(
        { id: initial.id, body },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        },
      )
    } else {
      const { enrollmentCode: __, webbillReceiverRef: ___, ...rest } = data
      createMutation.mutate(
        {
          ...rest,
          companyProfile,
          ...(data.visibility === 'INTERNAL' ? { enrollmentCode } : {}),
          ...(webbillReceiverRef ? { webbillReceiverRef } : {}),
        },
        {
          onSuccess: () => {
            onOpenChange(false)
          },
        },
      )
    }
  }

  const pending = createMutation.isPending || updateMutation.isPending
  const thumbnailUrl = form.watch('companyProfile.media.thumbnailUrl')
  const headerImageUrl = form.watch('companyProfile.media.headerImageUrl')

  const renderTextField = (
    name:
      | `companyProfile.legal.${'name' | 'uid' | 'registerId' | 'owner'}`
      | `companyProfile.legal.address.${'street' | 'postalCode' | 'city' | 'country'}`
      | `companyProfile.location.address.${'street' | 'postalCode' | 'city' | 'country'}`
      | `companyProfile.contact.${'phone' | 'email' | 'websiteUrl'}`
      | 'companyProfile.receipt.hoursText',
    label: string,
    placeholder?: string,
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Input
              placeholder={placeholder}
              {...field}
              value={field.value ?? ''}
              disabled={!canManage}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? tRestaurants('form.editTitle')
              : tRestaurants('form.createTitle')}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? tRestaurants('form.editDescription')
              : tRestaurants('form.createDescription')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tRestaurants('form.fields.name')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tRestaurants('form.placeholders.name')}
                      {...field}
                      disabled={!canManage}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tRestaurants('form.fields.code')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tRestaurants('form.placeholders.code')}
                      {...field}
                      disabled={!canManage || isEdit}
                      className="font-mono"
                    />
                  </FormControl>
                  <FormDescription>
                    {tRestaurants('form.descriptions.code')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{tRestaurants('form.fields.timezone')}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tRestaurants('form.placeholders.timezone')}
                      {...field}
                      disabled={!canManage}
                    />
                  </FormControl>
                  <FormDescription>
                    {tRestaurants('form.descriptions.timezone')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="onlineOrderingEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-md border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>
                      {tRestaurants('form.fields.onlineOrdering')}
                    </FormLabel>
                    <FormDescription>
                      {tRestaurants('form.descriptions.onlineOrdering')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!canManage}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="inventoryTrackingEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-md border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>
                      {tRestaurants('form.fields.inventoryTracking')}
                    </FormLabel>
                    <FormDescription>
                      {tRestaurants('form.descriptions.inventoryTracking')}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!canManage}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="inventoryCountShowExpected"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-md border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>
                      {tRestaurants('form.fields.inventoryCountShowExpected')}
                    </FormLabel>
                    <FormDescription>
                      {tRestaurants(
                        'form.descriptions.inventoryCountShowExpected',
                      )}
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!canManage}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="visibility"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {tRestaurants('form.fields.visibility')}
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!canManage}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="PUBLIC">
                        {tRestaurants('form.visibilityOptions.public')}
                      </SelectItem>
                      <SelectItem value="INTERNAL">
                        {tRestaurants('form.visibilityOptions.internal')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    {tRestaurants('form.descriptions.visibility')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* The webbill pseudo-GLN a delivery note is addressed to. Not behind the visibility
                switch: it is how the store receives supplier documents, which has nothing to do
                with whether customers can see it (CHG-2026-09-webbill-delivery-import). */}
            <FormField
              control={form.control}
              name="webbillReceiverRef"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {tRestaurants('form.fields.webbillReceiverRef')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={tRestaurants(
                        'form.placeholders.webbillReceiverRef',
                      )}
                      {...field}
                      disabled={!canManage}
                    />
                  </FormControl>
                  <FormDescription>
                    {tRestaurants('form.descriptions.webbillReceiverRef')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {visibility === 'INTERNAL' && (
              <FormField
                control={form.control}
                name="enrollmentCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {tRestaurants('form.fields.enrollmentCode')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={tRestaurants(
                          'form.placeholders.enrollmentCode',
                        )}
                        {...field}
                        disabled={!canManage}
                      />
                    </FormControl>
                    <FormDescription>
                      {tRestaurants('form.descriptions.enrollmentCode')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="defaultSafetyBufferQty"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {tRestaurants('form.fields.defaultSafetyBuffer')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      onChange={(e) =>
                        field.onChange(
                          Number.isNaN(parseInt(e.target.value))
                            ? 0
                            : parseInt(e.target.value),
                        )
                      }
                      disabled={!canManage}
                    />
                  </FormControl>
                  <FormDescription>
                    {tRestaurants('form.descriptions.defaultSafetyBuffer')}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Company profile: legal entity */}
            <div className="rounded-md border p-4">
              <div className="mb-4 space-y-1">
                <FormLabel className="text-base">
                  {tRestaurants('form.companyProfile.legalTitle')}
                </FormLabel>
                <FormDescription>
                  {tRestaurants('form.descriptions.companyProfile')}
                </FormDescription>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {renderTextField(
                  'companyProfile.legal.name',
                  tRestaurants('form.companyProfile.legalName'),
                  tRestaurants('form.placeholders.legalName'),
                )}
                {renderTextField(
                  'companyProfile.legal.uid',
                  tRestaurants('form.companyProfile.uid'),
                  tRestaurants('form.placeholders.uid'),
                )}
                {renderTextField(
                  'companyProfile.legal.registerId',
                  tRestaurants('form.companyProfile.registerId'),
                  tRestaurants('form.placeholders.registerId'),
                )}
                {renderTextField(
                  'companyProfile.legal.owner',
                  tRestaurants('form.companyProfile.owner'),
                  tRestaurants('form.placeholders.owner'),
                )}
              </div>

              <div className="mt-4 mb-2 text-sm font-medium">
                {tRestaurants('form.companyProfile.legalAddressTitle')}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {renderTextField(
                  'companyProfile.legal.address.street',
                  tRestaurants('form.companyProfile.street'),
                )}
                {renderTextField(
                  'companyProfile.legal.address.postalCode',
                  tRestaurants('form.companyProfile.postalCode'),
                )}
                {renderTextField(
                  'companyProfile.legal.address.city',
                  tRestaurants('form.companyProfile.city'),
                )}
                {renderTextField(
                  'companyProfile.legal.address.country',
                  tRestaurants('form.companyProfile.country'),
                )}
              </div>
            </div>

            {/* Company profile: location address (optional) */}
            <div className="rounded-md border p-4">
              <div className="mb-4 space-y-1">
                <FormLabel className="text-base">
                  {tRestaurants('form.companyProfile.locationTitle')}
                </FormLabel>
                <FormDescription>
                  {tRestaurants('form.descriptions.location')}
                </FormDescription>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {renderTextField(
                  'companyProfile.location.address.street',
                  tRestaurants('form.companyProfile.street'),
                )}
                {renderTextField(
                  'companyProfile.location.address.postalCode',
                  tRestaurants('form.companyProfile.postalCode'),
                )}
                {renderTextField(
                  'companyProfile.location.address.city',
                  tRestaurants('form.companyProfile.city'),
                )}
                {renderTextField(
                  'companyProfile.location.address.country',
                  tRestaurants('form.companyProfile.country'),
                )}
              </div>
            </div>

            {/* Company profile: contact */}
            <div className="rounded-md border p-4">
              <div className="mb-4">
                <FormLabel className="text-base">
                  {tRestaurants('form.companyProfile.contactTitle')}
                </FormLabel>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {renderTextField(
                  'companyProfile.contact.phone',
                  tRestaurants('form.companyProfile.phone'),
                )}
                {renderTextField(
                  'companyProfile.contact.email',
                  tRestaurants('form.companyProfile.email'),
                )}
                {renderTextField(
                  'companyProfile.contact.websiteUrl',
                  tRestaurants('form.companyProfile.websiteUrl'),
                )}
              </div>
            </div>

            {/* Company profile: printed URL slug */}
            <div className="rounded-md border p-4">
              <div className="mb-4">
                <FormLabel className="text-base">
                  {tRestaurants('form.companyProfile.urlSlugTitle')}
                </FormLabel>
              </div>
              <FormField
                control={form.control}
                name="companyProfile.urlSlug"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {tRestaurants('form.companyProfile.urlSlug')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={tRestaurants('form.placeholders.urlSlug')}
                        {...field}
                        value={field.value ?? ''}
                        disabled={!canManage}
                      />
                    </FormControl>
                    <FormDescription>
                      {tRestaurants('form.companyProfile.urlSlugHint')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Company profile: receipt + images */}
            <div className="rounded-md border p-4 space-y-4">
              <FormLabel className="text-base">
                {tRestaurants('form.companyProfile.receiptTitle')}
              </FormLabel>
              {renderTextField(
                'companyProfile.receipt.hoursText',
                tRestaurants('form.companyProfile.hoursText'),
                tRestaurants('form.placeholders.hoursText'),
              )}
              <div className="grid gap-4 md:grid-cols-2">
                <ImageUploadField
                  label={tRestaurants('form.companyProfile.thumbnail')}
                  description={tRestaurants('form.descriptions.thumbnail')}
                  value={thumbnailUrl || undefined}
                  onChange={(url) =>
                    form.setValue(
                      'companyProfile.media.thumbnailUrl',
                      url ?? '',
                    )
                  }
                  disabled={!canManage}
                />
                <ImageUploadField
                  label={tRestaurants('form.companyProfile.headerImage')}
                  description={tRestaurants('form.descriptions.headerImage')}
                  value={headerImageUrl || undefined}
                  onChange={(url) =>
                    form.setValue(
                      'companyProfile.media.headerImageUrl',
                      url ?? '',
                    )
                  }
                  disabled={!canManage}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {tCommon('cancel')}
              </Button>
              <Button type="submit" disabled={pending || !canManage}>
                {pending
                  ? isEdit
                    ? tCommon('saving')
                    : tCommon('creating')
                  : isEdit
                    ? tCommon('saveChanges')
                    : tCommon('create')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
